/**
 * Event in a Box audience links (migration 2026-10-16-894).
 *
 * After an EIB launch commits, a database sweep (pg_cron, every 30 s; never inside the commit) links the
 * launch's frozen audience snapshot to the event in `event_lists` and marks the row with `eib_launch_id`. Those rows are display-only (nothing sends to or
 * enrolls from `event_lists`) and are owned by the launch: the database skips a client delete of them and
 * refuses a client update. The UI therefore shows them read-only and keeps them out of the event form's
 * editable segment selection.
 */

export interface EventListRow {
  list_id: string;
  eib_launch_id?: string | null;
  lists?: {
    id?: string;
    name?: string | null;
    list_type?: string | null;
    contact_count?: number | null;
  } | null;
}

export function isEibAudienceLink(row: {
  eib_launch_id?: string | null;
}): boolean {
  return typeof row.eib_launch_id === "string" && row.eib_launch_id.length > 0;
}

/** "Event in a Box audience (snapshot, 17,680 contacts)" -- the count is omitted when unknown. */
export function eibAudienceLabel(row: EventListRow): string {
  const count = row.lists?.contact_count;
  const countText =
    typeof count === "number"
      ? `, ${count.toLocaleString()} contact${count === 1 ? "" : "s"}`
      : "";
  return `Event in a Box audience (snapshot${countText})`;
}

/** Deep link into the Segments page that opens this exact list. */
export function segmentHref(listId: string): string {
  return `/segments?segment=${encodeURIComponent(listId)}`;
}

/** EIB audiences first (oldest launch first), then the hand-linked lists in their original order. */
export function sortEventListRows<T extends EventListRow>(rows: T[]): T[] {
  const eib = rows.filter(isEibAudienceLink);
  const plain = rows.filter((row) => !isEibAudienceLink(row));
  return [...eib, ...plain];
}
